import * as PropTypes from "prop-types";

import { promisify } from "../../../utilities";
import { useState } from "react";

import { RS } from "../../../data/strings/global";
import * as SC from "../../../data/strings/PIStringConst";

import * as Theme from "../../../app/Theme";

import * as gbtu from "../../GB/GBTableUtil";
import * as piasu from "../NonComponents/PIAppStateUtil";
import * as pisc from "../NonComponents/PIServerConst";
import * as gbtc from "../../GB/GBTableConst";
import * as gbu from "../../GB/GBUtil";

import GbStdTableWithComboBoxes from "../../GB/GbStdTableWithComboBoxes";
import * as pitu from "../NonComponents/PITableUtil";

import { generateHeterogeneousTypes } from "../../../utilities";

import { combinePriorPops, disaggregatePriorPops, contCurveCombinedPops } from "../NonComponents/PIPriorPopUtil";

const priorPopCurrIDC = "priorPopCurrID";
const methodCurrIDC = "methodCurrID";

const PIAssignScaleUpTrendsToPriorPopsTable = (props) => {
  const onModVarsChange = promisify(props.onModVarsChange);

  const modVarObjList = props.modVarObjList;
  const isPSEMode = piasu.isPSEMode(modVarObjList);

  const scaleUpTrendsObjList = piasu.getModVarValue(modVarObjList, "PI_ScaleupTrends");
  const methods = piasu.getModVarValue(modVarObjList, "PI_Methods");

  let combinedTest;
  let priorPops = piasu.getModVarValue(modVarObjList, "PI_PriorityPop");
  let eligibility = piasu.getModVarValue(modVarObjList, "PI_Eligibility");

  if (isPSEMode) {
    combinedTest = combinePriorPops(modVarObjList, contCurveCombinedPops, ["PI_Eligibility"]);
    priorPops = combinedTest["PI_PriorityPop"];
    eligibility = combinedTest["PI_Eligibility"];
  }

  // State
  const [focusedCell, setFocusedCell] = useState({
    row: 0,
    col: 1,
  });

  const [selectedRegions, setSelectedRegions] = useState({
    rowStart: 0,
    rowEnd: 0,
    columnStart: 1,
    columnEnd: 1,
  });

  // Callbacks
  const onScaleUpTrendComboBoxChange = async (value, text, info) => {
    const newData = {
      PI_PriorityPop: structuredClone(priorPops),
    };

    const scaleUpTrendCurrID = value + 1;
    const priorPopCurrID = info[priorPopCurrIDC];
    const methodCurrID = info[methodCurrIDC];

    const mstID = piasu.getScaleUpTrendMstID(scaleUpTrendsObjList, scaleUpTrendCurrID);
    piasu.setPriorPopScaleUpTrendMstID(newData.PI_PriorityPop, methodCurrID, priorPopCurrID, mstID);

    const newModVars = structuredClone(modVarObjList);

    if (isPSEMode) {
      const disagg = disaggregatePriorPops(modVarObjList, newData, contCurveCombinedPops);
      piasu.setModVarValue(newModVars, "PI_PriorityPop", disagg.PI_PriorityPop);
    } else {
      piasu.setModVarValue(newModVars, "PI_PriorityPop", newData.PI_PriorityPop);
    }

    await onModVarsChange(newModVars, false);
  };

  // Table
  const numRows = priorPops.length + 1; // includes one header

  const firstRow = 0;

  const priorPopCol = 0;
  const numCols = methods.length + 1;

  const packTable = gbtu.resizePackTable(gbtu.getNewPackTable(), numRows, numCols);

  /* Set column headings */
  gbtu.setValue(packTable, firstRow, priorPopCol, RS(SC.GB_stPriorityPop));

  for (let m = 1; m <= methods.length; m++) {
    gbtu.setValue(packTable, firstRow, m, piasu.methodName(methods, m));
  }

  let comboBoxObj = pitu.getComboBoxObj(numRows, numCols);

  for (let pp = 1; pp <= priorPops.length; pp++) {
    /* Set row headings. */
    const priorPopName = piasu.getPriorPopName(priorPops, pp);
    gbtu.setValue(packTable, pp, priorPopCol, priorPopName);

    for (let m = 1; m <= methods.length; m++) {
      const methodMstID = piasu.methodMstID(methods, m);
      const methodEligMstIDStr = piasu.getPriorPopMethodElig(eligibility, methodMstID, pp);

      if (methodEligMstIDStr !== pisc.yesCVOMstID) {
        gbtu.lockCell(packTable, pp, m, true, true);
        const gainsboroBase10 = gbu.toBase10(gbu.getDelphiHexFromHexColor(Theme.whisperGrayTableColor));
        gbtu.setCellBGColor(packTable, pp, m, gainsboroBase10);

        continue;
      }

      gbtu.setHasComboBox(packTable, pp, m, true);

      comboBoxObj[pitu.style2DObjArray][pp][m] = {
        color: Theme.blackColor,
        fontFamily: Theme.fontFamily,
      };

      let priorPopCurrIDArray = [];
      priorPopCurrIDArray.length = piasu.getTotalNumScaleUpTrends(scaleUpTrendsObjList);
      priorPopCurrIDArray.fill({
        [priorPopCurrIDC]: pp,
        [methodCurrIDC]: m,
      });
      comboBoxObj[pitu.info3DIntArray][pp][m] = priorPopCurrIDArray;

      comboBoxObj[pitu.item3DStrArray][pp][m] = piasu.getScaleUpTrendNames(scaleUpTrendsObjList);

      const scaleUpTrendMstID = piasu.getPriorPopScaleUpTrendMstID(priorPops, m, pp);
      const scaleUpTrendCurrID = piasu.getScaleUpTrendCurrID(scaleUpTrendsObjList, scaleUpTrendMstID); // markProblem
      comboBoxObj[pitu.idx2DIntArray][pp][m] = scaleUpTrendCurrID - 1;

      comboBoxObj[pitu.onChange2DFuncArray][pp][m] = onScaleUpTrendComboBoxChange;
    }
  }

  gbtu.alignNumericCellsRight(packTable);
  gbtu.setRowAlignment(packTable, firstRow, gbtc.hAlign.center);
  gbtu.setRDecs(packTable, 0);
  gbtu.setColWidths(packTable, Theme.itemNameColWidth);
  gbtu.setColWidth(packTable, priorPopCol, Theme.itemNameColWidthWide);
  gbtu.setWordWrappedCols(packTable, true);
  gbtu.setMinAllowedValForTable(packTable, 0);
  gbtu.setMaxAllowedValForTable(packTable, 0);
  gbtu.setID(packTable, "assignScaleUpTrendsToPriorPopsPackTable");
  gbtu.setKey(packTable, "assignScaleUpTrendsToPriorPopsPackTable");

  return (
    <GbStdTableWithComboBoxes
      focusedCell={focusedCell}
      onCellFocused={(focusedCell) => setFocusedCell(focusedCell)}
      font={Theme.tableFont}
      headerBackgroundColor={Theme.PI_PrimaryColor}
      gridKey={props.tableKey}
      oddRowBackgroundColor={Theme.PI_BandColor}
      packTable={packTable}
      types={generateHeterogeneousTypes(packTable, "dd")}
      onPackTableChanged={() => {}}
      removedMenuNames={pitu.tableHideMenuItems}
      selectedRegions={selectedRegions}
      onSelectionChanged={(selectedRegions) => setSelectedRegions(selectedRegions)}
      style={{
        tableFont: Theme.tableFont,
        marginLeft: Theme.leftIndent,
        marginTop: Theme.ctrlSpacing,
        padding: 0,
      }}
      comboBoxStyle={comboBoxObj[pitu.style2DObjArray]}
      comboBoxInfo={comboBoxObj[pitu.info3DIntArray]}
      comboBoxItems={comboBoxObj[pitu.item3DStrArray]}
      comboBoxIndices={comboBoxObj[pitu.idx2DIntArray]}
      onComboBoxChange={comboBoxObj[pitu.onChange2DFuncArray]}
      width={0}
      limitToTableWidth={true}
    />
  );
};

PIAssignScaleUpTrendsToPriorPopsTable.propTypes = {
  modVarObjList: PropTypes.array.isRequired,
  onModVarsChange: PropTypes.func,
  onDialogChange: PropTypes.func,

  tableKey: PropTypes.string,
};

PIAssignScaleUpTrendsToPriorPopsTable.defaultProps = {
  onModVarsChange: () => {},
  onDialogChange: () => {},

  tableKey: "",
};

export default PIAssignScaleUpTrendsToPriorPopsTable;
